import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateSpaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { spa } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (!spa || isUndefined(spa.hasFeature) || isUndefined(spa.quality) || isUndefined(zipCode) || isUndefined(spa.area)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getSpaCost({
    hasFeature: ORDER_FEATURES.spa.hasFeature(improvementsForTransaction),
    sqft: AloftArea.schema.parse(spa.area),
    quality: spa.quality,
    zipCode,
  });
};

const getTotalOrBreakdownSpaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateSpaCost(costProps, improvements, propertyIdentification, id);
  if (!costOutput) return AloftPrice.zero;

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalSpaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => getTotalOrBreakdownSpaCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectSpaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalSpaCost,
);

export const selectZipOnlySpaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownSpaCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectSpaCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectCompSequence,
  ],
  (costProps, improvements, propertyIdentification, compSequence) =>
    ['subject', ...compSequence].reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalSpaCost(costProps, improvements, propertyIdentification, propID);
      if (cost) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectSpaCostDisplay = createSelector([selectSpaCost], (spaCost) =>
  spaCost?.toString(true),
);
